import { useState, useRef, useEffect } from 'react'
import { Html5Qrcode } from 'html5-qrcode'
import { Upload, Camera, X } from 'lucide-react'
import Card from '../components/Card'
import Button from '../components/Button'

export default function QRScanner() {
  const [scanResult, setScanResult] = useState(null)
  const [isScanning, setIsScanning] = useState(false)
  const [error, setError] = useState(null)
  const scannerRef = useRef(null)
  const html5QrCodeRef = useRef(null)
  const fileInputRef = useRef(null)

  useEffect(() => {
    return () => {
      if (html5QrCodeRef.current) {
        html5QrCodeRef.current.stop().catch(() => {})
      }
    }
  }, [])

  const startScanning = async () => {
    try {
      setError(null)
      setScanResult(null)
      
      const html5QrCode = new Html5Qrcode('qr-reader')
      html5QrCodeRef.current = html5QrCode
      
      await html5QrCode.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: { width: 250, height: 250 }
        },
        (decodedText) => {
          setScanResult(decodedText)
          stopScanning()
        },
        (errorMessage) => {
          // Ignore scanning errors
        }
      )
      
      setIsScanning(true)
    } catch (err) {
      setError('Failed to start camera: ' + err.message)
      setIsScanning(false)
    }
  }

  const stopScanning = () => {
    if (html5QrCodeRef.current) {
      html5QrCodeRef.current.stop().then(() => {
        html5QrCodeRef.current.clear()
        html5QrCodeRef.current = null
        setIsScanning(false)
      }).catch(() => {
        setIsScanning(false)
      })
    }
  }

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return

    try {
      setError(null)
      setScanResult(null)
      
      const html5QrCode = new Html5Qrcode('qr-reader')
      const result = await html5QrCode.scanFile(file, false)
      setScanResult(result)
    } catch (err) {
      setError('Failed to scan QR code: ' + err.message)
    }
  }

  return (
    <div className="space-y-6">
      <Card>
        <Card.Body>
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Scan QR Code</h3>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                onClick={isScanning ? stopScanning : startScanning}
                variant="primary"
                className="flex-1"
              >
                <Camera className="w-5 h-5 mr-2" />
                {isScanning ? 'Stop Scanning' : 'Start Camera Scan'}
              </Button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
                id="qr-file-input"
              />
              <Button
                variant="outline"
                className="w-full sm:w-auto"
                onClick={() => fileInputRef.current?.click()}
                type="button"
              >
                <Upload className="w-5 h-5 mr-2" />
                Upload Image
              </Button>
            </div>
          </div>

          <div id="qr-reader" className="w-full max-w-md mx-auto"></div>

          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}
        </Card.Body>
      </Card>

      {scanResult && (
        <Card>
          <Card.Body>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Scanned Result:</h3>
                <button
                  onClick={() => setScanResult(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-600" />
                </button>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-900 break-all">{scanResult}</p>
              </div>
              <div className="flex gap-4">
                <Button
                  onClick={() => {
                    if (scanResult.startsWith('http://') || scanResult.startsWith('https://')) {
                      window.open(scanResult, '_blank')
                    }
                  }}
                  variant="primary"
                  className="flex-1"
                  disabled={!scanResult.startsWith('http://') && !scanResult.startsWith('https://')}
                >
                  Open URL
                </Button>
                <Button
                  onClick={() => {
                    navigator.clipboard.writeText(scanResult)
                    alert('Copied to clipboard!')
                  }}
                  variant="outline"
                  className="flex-1"
                >
                  Copy Text
                </Button>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {!scanResult && !isScanning && (
        <Card>
          <Card.Body className="text-center py-12">
            <p className="text-gray-600">Start camera scanning or upload an image to scan QR code</p>
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

